import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { getGroupData } from '../../../api/supabase/codeService';

interface HomeschoolPriceData {
  original: number;
  discount: number;
  monthly: number;
}

interface HomeschoolExtensionPriceData {
  bookYearly: number;
  bookMonthly: number;
  onlineYearly: number;
  onlineMonthly: number;
}

interface HomeschoolPrices {
  kids: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
    extension: HomeschoolExtensionPriceData;
  };
  basic: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
    extension: HomeschoolExtensionPriceData;
  };
  advanced: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
    extension: HomeschoolExtensionPriceData;
  };
  kidsBasic: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  basicAdvanced: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
  allInOne: {
    one: HomeschoolPriceData;
    two: HomeschoolPriceData;
  };
}

const HOMESCHOOL_DEFAULT_PRICES: HomeschoolPrices = {
  kids: {
    one: { original: 989000, discount: 890000, monthly: 74100 },
    two: { original: 1385000, discount: 1070000, monthly: 44500 },
    extension: {
      bookYearly: 420000,
      bookMonthly: 35000,
      onlineYearly: 120000,
      onlineMonthly: 10000,
    },
  },
  basic: {
    one: { original: 1804000, discount: 1570000, monthly: 130800 },
    two: { original: 2614000, discount: 2070000, monthly: 86200 },
    extension: {
      bookYearly: 720000,
      bookMonthly: 60000,
      onlineYearly: 360000,
      onlineMonthly: 30000,
    },
  },
  advanced: {
    one: { original: 828000, discount: 770000, monthly: 64100 },
    two: { original: 1138000, discount: 910000, monthly: 37900 },
    extension: {
      bookYearly: 190000,
      bookMonthly: 15800,
      onlineYearly: 120000,
      onlineMonthly: 10000,
    },
  },
  kidsBasic: {
    one: { original: 2553000, discount: 1970000, monthly: 164100 },
    two: { original: 3783000, discount: 2570000, monthly: 107000 },
  },
  basicAdvanced: {
    one: { original: 2512000, discount: 1920000, monthly: 160000 },
    two: { original: 3632000, discount: 2320000, monthly: 96600 },
  },
  allInOne: {
    one: { original: 3291000, discount: 2420000, monthly: 201600 },
    two: { original: 4801000, discount: 3020000, monthly: 125800 },
  },
};

interface HomeschoolPriceContextType {
  prices: HomeschoolPrices | null;
  isLoading: boolean;
  error: string | null;
}

const HomeschoolPriceContext = createContext<
  HomeschoolPriceContextType | undefined
>(undefined);

// -----------------------------------------------------------------------------
//  캐싱 로직
//  여러 Provider 가 중첩되어도 Supabase 요청이 중복되지 않도록 모듈 레벨에
//  가격 데이터를 보관합니다.
// -----------------------------------------------------------------------------

let cachedPrices: HomeschoolPrices | null = null;
let cachedError: string | null = null;
let cachedFetchPromise: Promise<void> | null = null;

export const HomeschoolPriceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [prices, setPrices] = useState<HomeschoolPrices | null>(
    HOMESCHOOL_DEFAULT_PRICES,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getPrice = (
    priceData: Record<string, string> | null,
    key: string,
    defaultValue: number,
  ) => {
    const raw = priceData?.[key];
    if (raw === undefined || raw === null || raw === '') {
      return defaultValue;
    }
    const value = Number(raw);
    return Number.isNaN(value) ? defaultValue : value;
  };

  useEffect(() => {
    // 이미 캐시가 존재하면 바로 사용하고 종료
    if (cachedPrices) {
      setPrices(cachedPrices);
      setIsLoading(false);
      setError(cachedError);
      return;
    }

    // fetch 가 이미 진행중이면 그 Promise 에 맞춰서 상태 업데이트만 수행
    if (cachedFetchPromise) {
      cachedFetchPromise.then(() => {
        setPrices(cachedPrices);
        setError(cachedError);
        setIsLoading(false);
      });
      return;
    }

    const fetchPrices = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await getGroupData('COURSE');

        const d = HOMESCHOOL_DEFAULT_PRICES;

        const priceData: HomeschoolPrices = {
          kids: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_KIDS_ONE_ORIGINAL',
                d.kids.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_KIDS_ONE_DISCOUNT',
                d.kids.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_KIDS_ONE_MONTHLY',
                d.kids.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_KIDS_TWO_ORIGINAL',
                d.kids.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_KIDS_TWO_DISCOUNT',
                d.kids.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_KIDS_TWO_MONTHLY',
                d.kids.two.monthly,
              ),
            },
            extension: {
              bookYearly: getPrice(
                res,
                'HOMESCHOOL_KIDS_EXTENSION_BOOK_YEARLY',
                d.kids.extension.bookYearly,
              ),
              bookMonthly: getPrice(
                res,
                'HOMESCHOOL_KIDS_EXTENSION_BOOK_MONTHLY',
                d.kids.extension.bookMonthly,
              ),
              onlineYearly: getPrice(
                res,
                'HOMESCHOOL_KIDS_EXTENSION_ONLINE_YEARLY',
                d.kids.extension.onlineYearly,
              ),
              onlineMonthly: getPrice(
                res,
                'HOMESCHOOL_KIDS_EXTENSION_ONLINE_MONTHLY',
                d.kids.extension.onlineMonthly,
              ),
            },
          },
          basic: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_BASIC_ONE_ORIGINAL',
                d.basic.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_BASIC_ONE_DISCOUNT',
                d.basic.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_BASIC_ONE_MONTHLY',
                d.basic.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_BASIC_TWO_ORIGINAL',
                d.basic.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_BASIC_TWO_DISCOUNT',
                d.basic.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_BASIC_TWO_MONTHLY',
                d.basic.two.monthly,
              ),
            },
            extension: {
              bookYearly: getPrice(
                res,
                'HOMESCHOOL_BASIC_EXTENSION_BOOK_YEARLY',
                d.basic.extension.bookYearly,
              ),
              bookMonthly: getPrice(
                res,
                'HOMESCHOOL_BASIC_EXTENSION_BOOK_MONTHLY',
                d.basic.extension.bookMonthly,
              ),
              onlineYearly: getPrice(
                res,
                'HOMESCHOOL_BASIC_EXTENSION_ONLINE_YEARLY',
                d.basic.extension.onlineYearly,
              ),
              onlineMonthly: getPrice(
                res,
                'HOMESCHOOL_BASIC_EXTENSION_ONLINE_MONTHLY',
                d.basic.extension.onlineMonthly,
              ),
            },
          },
          advanced: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_ADV_ONE_ORIGINAL',
                d.advanced.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_ADV_ONE_DISCOUNT',
                d.advanced.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_ADV_ONE_MONTHLY',
                d.advanced.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_ADV_TWO_ORIGINAL',
                d.advanced.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_ADV_TWO_DISCOUNT',
                d.advanced.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_ADV_TWO_MONTHLY',
                d.advanced.two.monthly,
              ),
            },
            extension: {
              bookYearly: getPrice(
                res,
                'HOMESCHOOL_ADV_EXTENSION_BOOK_YEARLY',
                d.advanced.extension.bookYearly,
              ),
              bookMonthly: getPrice(
                res,
                'HOMESCHOOL_ADV_EXTENSION_BOOK_MONTHLY',
                d.advanced.extension.bookMonthly,
              ),
              onlineYearly: getPrice(
                res,
                'HOMESCHOOL_ADV_EXTENSION_ONLINE_YEARLY',
                d.advanced.extension.onlineYearly,
              ),
              onlineMonthly: getPrice(
                res,
                'HOMESCHOOL_ADV_EXTENSION_ONLINE_MONTHLY',
                d.advanced.extension.onlineMonthly,
              ),
            },
          },
          kidsBasic: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_ONE_ORIGINAL',
                d.kidsBasic.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_ONE_DISCOUNT',
                d.kidsBasic.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_ONE_MONTHLY',
                d.kidsBasic.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_TWO_ORIGINAL',
                d.kidsBasic.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_TWO_DISCOUNT',
                d.kidsBasic.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_KIDS_BASIC_TWO_MONTHLY',
                d.kidsBasic.two.monthly,
              ),
            },
          },
          basicAdvanced: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_ONE_ORIGINAL',
                d.basicAdvanced.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_ONE_DISCOUNT',
                d.basicAdvanced.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_ONE_MONTHLY',
                d.basicAdvanced.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_TWO_ORIGINAL',
                d.basicAdvanced.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_TWO_DISCOUNT',
                d.basicAdvanced.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_BASIC_ADV_TWO_MONTHLY',
                d.basicAdvanced.two.monthly,
              ),
            },
          },
          allInOne: {
            one: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_ONE_ORIGINAL',
                d.allInOne.one.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_ONE_DISCOUNT',
                d.allInOne.one.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_ONE_MONTHLY',
                d.allInOne.one.monthly,
              ),
            },
            two: {
              original: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_TWO_ORIGINAL',
                d.allInOne.two.original,
              ),
              discount: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_TWO_DISCOUNT',
                d.allInOne.two.discount,
              ),
              monthly: getPrice(
                res,
                'HOMESCHOOL_COMBINED_ALLINONE_TWO_MONTHLY',
                d.allInOne.two.monthly,
              ),
            },
          },
        };

        // 캐시에 저장
        cachedPrices = priceData;
        cachedError = null;

        setPrices(priceData);
      } catch (error) {
        console.error('COURSE 데이터 조회 실패:', error);
        cachedError = '가격 데이터를 불러오는데 실패했습니다.';
        setError(cachedError);
        setIsLoading(false);
      } finally {
        setIsLoading(false);
        cachedFetchPromise = null; // fetch 완료
      }
    };

    cachedFetchPromise = fetchPrices();
  }, []);

  return (
    <HomeschoolPriceContext.Provider value={{ prices, isLoading, error }}>
      {children}
    </HomeschoolPriceContext.Provider>
  );
};

export const useHomeschoolPrices = () => {
  const context = useContext(HomeschoolPriceContext);
  if (!context) {
    throw new Error(
      'useHomeschoolPrices must be used within a HomeschoolPriceProvider',
    );
  }
  return context;
};
